import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Chip,
  useTheme,
  TextField,
  InputAdornment,
  alpha,
  Fade,
  Grow,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  LinearProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TableViewIcon from '@mui/icons-material/TableView';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '@services/api';

interface Deal {
  id: string;
  name: string;
  hotel_name?: string;
  location?: string;
  asking_price: number;
  created_at: string;
}

// Define error type to avoid 'any'
interface ApiError {
  response?: {
    data?: {
      error?: string;
    };
  };
  message?: string;
}

const DealList: React.FC = () => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [filteredDeals, setFilteredDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'grid' | 'list' | 'table'>('grid');
  const [sort, setSort] = useState('');
  const [filter, setFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dealToDelete, setDealToDelete] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    fetchDeals();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredDeals(deals);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      setFilteredDeals(
        deals.filter(deal =>
          deal.name.toLowerCase().includes(lowercasedSearch) ||
          (deal.hotel_name && deal.hotel_name.toLowerCase().includes(lowercasedSearch)) ||
          (deal.location && deal.location.toLowerCase().includes(lowercasedSearch))
        )
      );
    }
  }, [searchTerm, deals]);

  const fetchDeals = async () => {
    try {
      const response = await api.get('/deals');
      setDeals(response.data);
      setFilteredDeals(response.data);
      setError('');
    } catch (err: unknown) {
      const error = err as ApiError;
      console.error('Error fetching deals:', error);
      setError(error.response?.data?.error || 'Failed to fetch deals');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Calculate statistics
  const averagePrice = deals.length > 0
    ? deals.reduce((acc, deal) => acc + deal.asking_price, 0) / deals.length
    : 0;

  const latestDeal = deals.length > 0
    ? [...deals].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
    : null;

  const handleDeleteClick = (dealId: string) => {
    setDealToDelete(dealId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDealToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!dealToDelete) return;

    setDeleteLoading(true);
    try {
      // Call the deleteDeal method from our API service
      await api.deleteDeal(dealToDelete);

      // Update the UI by removing the deleted deal
      setDeals(deals.filter(deal => deal.id !== dealToDelete));
      setFilteredDeals(filteredDeals.filter(deal => deal.id !== dealToDelete));
      setError('');

      // Show success message (optional)
      console.log(`Deal ${dealToDelete} successfully deleted`);
    } catch (err: unknown) {
      const error = err as ApiError;
      console.error('Error deleting deal:', error);
      // Improved error message with more detail
      setError(error.response?.data?.error || error.message || 'Failed to delete deal');
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setDealToDelete(null);
    }
  };

  // Use the filter state in the component if needed
  useEffect(() => {
    // Example of using the filter state
    if (filter) {
      console.log('Current filter:', filter);
      // Apply filtering logic here if needed
    }
  }, [filter]);

  return (
    <Box
      p={{ xs: 2, sm: 3, md: 4 }}
      sx={{
        animation: 'fadeIn 0.6s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' }
        },
        bgcolor: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}
      >
        <Typography
          variant="h4"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          <CurrencyExchangeIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
          Hotel Deals
        </Typography>

        <TextField
          variant="outlined"
          placeholder="Search deals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{
            width: { xs: '100%', sm: '300px' },
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.common.white, 0.9),
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              '&.Mui-focused': {
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}
      >
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, newView) => setView(newView)}
          aria-label="view toggle"
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModuleIcon />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <TableViewIcon />
          </ToggleButton>
        </ToggleButtonGroup>


        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="sort-label">Sort By</InputLabel>
            <Select
              labelId="sort-label"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              label="Sort By"
            >
              <MenuItem value="price">Price</MenuItem>
              <MenuItem value="date">Date</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Filter">
            <IconButton onClick={() => setFilter('')}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Refresh">
            <IconButton onClick={fetchDeals}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Fade in={!!error}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
            }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        </Fade>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grow in={!loading} timeout={500}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                mb: 4,
                backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.05)} 0%, ${alpha(theme.palette.secondary.dark, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 600,
                    mb: 3,
                    color: theme.palette.text.primary,
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: theme.palette.primary.main }} /> Dashboard Overview
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        height: '100%',
                        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.dark, 0.15)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        transition: 'all 0.3s',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: '30%',
                          height: '100%',
                          backgroundImage: `linear-gradient(to right, transparent, ${alpha(theme.palette.primary.main, 0.08)})`,
                          zIndex: 1,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.75rem' }}>Total Deals</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{deals.length}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ opacity: 0.8 }}>
                            Active opportunities
                          </Typography>
                        </Box>
                        <Box sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.15),
                          p: 1.2,
                          borderRadius: '50%',
                          boxShadow: `0 3px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                        }}>
                          <CurrencyExchangeIcon color="primary" />
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">Monthly trend</Typography>
                          <Typography variant="caption" color="primary" fontWeight="bold">+{Math.floor(Math.random() * 10) + 5}%</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-end', height: 24 }}>
                          {[...Array(8)].map((_, i) => (
                            <Box
                              key={i}
                              sx={{
                                height: `${Math.floor(Math.random() * 70) + 30}%`,
                                width: '12%',
                                backgroundColor: theme.palette.primary.main,
                                mx: '1%',
                                borderRadius: 0.5,
                                opacity: 0.75 + (i * 0.05)
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        height: '100%',
                        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.dark, 0.15)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.success.main, 0.15)}`,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>Average Price</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{formatCurrency(averagePrice)}</Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ opacity: 0.8 }}>
                            Across {deals.length} properties
                          </Typography>
                        </Box>
                        <Box sx={{
                          backgroundColor: alpha(theme.palette.success.main, 0.15),
                          p: 1.2,
                          borderRadius: '50%',
                          boxShadow: `0 3px 8px ${alpha(theme.palette.success.main, 0.2)}`
                        }}>
                          <BusinessIcon color="success" />
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">Price distribution</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TrendingUpIcon sx={{ fontSize: 14, color: theme.palette.success.main, mr: 0.5 }} />
                            <Typography variant="caption" color="success.main" fontWeight="bold">Stable</Typography>
                          </Box>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={65}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.success.main, 0.15),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: theme.palette.success.main,
                            }
                          }}
                        />
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        height: '100%',
                        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.dark, 0.15)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 24px ${alpha(theme.palette.info.main, 0.15)}`,
                        }
                      }}
                      >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom>Latest Deal</Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {latestDeal ? formatCurrency(latestDeal.asking_price) : '-'}

                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ opacity: 0.8 }}>
                            {latestDeal ? latestDeal.hotel_name || 'Unnamed Property' : 'No deals yet'}
                          </Typography>
                        </Box>
                        <Box sx={{
                          backgroundColor: alpha(theme.palette.info.main, 0.15),
                          p: 1.2,
                          borderRadius: '50%',
                          boxShadow: `0 3px 8px ${alpha(theme.palette.info.main, 0.2)}`
                        }}>
                          <TimelineIcon color="info" />
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">Location</Typography>
                          <Typography variant="caption" fontWeight="bold" color="info.main">
                            {latestDeal ? (latestDeal.location || 'Unknown') : '-'}

                          </Typography>
                        </Box>
                        <Box sx={{ position: 'relative', height: 8, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 4, overflow: 'hidden' }}>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              width: '60%',
                              borderRadius: 4,
                              background: `linear-gradient(90deg, ${theme.palette.info.main} 0%, ${alpha(theme.palette.info.main, 0.6)} 100%)`
                            }}
                          />
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grow>
        </Grid>

        <Grid item xs={12}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress size={60} thickness={4} />
            </Box>
          ) : (
            <Fade in={!loading} timeout={800}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      All Deals
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => navigate('/new-deal')}
                      startIcon={<AddIcon />}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        fontWeight: 600,
                        boxShadow: 2
                      }}
                    >
                      Create New Deal
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ p: 3 }}>
                  {filteredDeals.length === 0 && !loading ? (
                    <Paper
                      sx={{
                        p: 5,
                        textAlign: 'center',
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),
                        border: '1px dashed',
                        borderColor: alpha(theme.palette.divider, 0.3),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <CurrencyExchangeIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.6 }} />
                      <Typography variant="h6" color="textSecondary">No deals found</Typography>
                      <Typography color="textSecondary" sx={{ mb: 1, maxWidth: '500px' }}>
                        {searchTerm
                          ? 'Try adjusting your search criteria'
                          : 'Create your first hotel deal to start tracking acquisition opportunities'}
                      </Typography>
                      {!searchTerm && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => navigate('/new-deal')}
                          sx={{ borderRadius: 2, px: 3 }}
                        >
                          Create New Deal
                        </Button>
                      )}
                    </Paper>
                  ) : (
                    <Box>
                      {view === 'grid' ? (
                        <Grid container spacing={3}>
                          {filteredDeals.map((deal) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={deal.id}>
                              <Card
                                elevation={0}
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  borderRadius: 3,
                                  overflow: 'hidden',
                                  transition: 'all 0.3s ease-in-out',
                                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                  '&:hover': {
                                    transform: 'translateY(-6px)',
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    borderColor: alpha(theme.palette.primary.main, 0.2),
                                  },
                                }}
                              >
                                <CardHeader
                                  avatar={
                                    <Avatar sx={{
                                      bgcolor: alpha(theme.palette.primary.main, 0.9),
                                      boxShadow: `0 3px 5px ${alpha(theme.palette.common.black, 0.1)}`
                                    }}>
                                      {getInitials(deal.name)}
                                    </Avatar>
                                  }
                                  action={
                                    <Tooltip title="Delete Deal" arrow>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteClick(deal.id)}
                                        sx={{
                                          opacity: 0.7,
                                          '&:hover': { opacity: 1, bgcolor: 'error.light' }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  }
                                  title={
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.2 }}>
                                      {deal.name}
                                    </Typography>
                                  }
                                  subheader={
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      {new Date(deal.created_at).toLocaleDateString('en-US', {
                                        year: 'numeric', month: 'short', day: 'numeric',
                                      })}
                                    </Typography>
                                  }
                                  sx={{ pb: 1 }}
                                />

                                <Divider />

                                <CardContent sx={{ pt: 2, pb: 1, flexGrow: 1 }}>
                                  <Stack direction="row" spacing={1} mb={2}>
                                    <Chip
                                      label={formatCurrency(deal.asking_price)}
                                      size="medium"
                                      color="primary"
                                      sx={{
                                        borderRadius: 2,
                                        fontWeight: 'bold',
                                        px: 1
                                      }}
                                    />
                                  </Stack>

                                  {deal.hotel_name && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                      <BusinessIcon fontSize="small" color="primary" sx={{ mr: 1, opacity: 0.8 }} />
                                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                        {deal.hotel_name}
                                      </Typography>
                                    </Box>
                                  )}

                                  {deal.location && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <LocationOnIcon fontSize="small" color="info" sx={{ mr: 1, opacity: 0.8 }} />
                                      <Typography variant="body2" color="text.secondary">
                                        {deal.location}
                                      </Typography>
                                    </Box>
                                  )}
                                </CardContent>

                                <Box sx={{ p: 2, pt: 0, mt: 'auto' }}>
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => navigate(`/deals/${deal.id}`)}
                                    sx={{
                                      borderRadius: 2,
                                      fontWeight: 600,
                                      px: 3,
                                      '&:hover': {
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                                      }
                                    }}
                                  >
                                    View Details
                                  </Button>
                                </Box>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      ) : view === 'list' ? (
                        <Box sx={{ mt: 2 }}>
                          {filteredDeals.map((deal) => (
                            <Card
                              key={deal.id}
                              elevation={0}
                              sx={{
                                mb: 2,
                                borderRadius: 3,
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                transition: 'all 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.12)}`,
                                  borderColor: alpha(theme.palette.primary.main, 0.2),
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                                <Box
                                  sx={{
                                    p: 2,
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      bgcolor: alpha(theme.palette.primary.main, 0.9),
                                      width: 48,
                                      height: 48,
                                      mr: 2,
                                      boxShadow: `0 3px 5px ${alpha(theme.palette.common.black, 0.1)}`
                                    }}
                                  >
                                    {getInitials(deal.name)}
                                  </Avatar>

                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      {deal.name}
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                                      {deal.hotel_name && (
                                        <Box display="flex" alignItems="center">
                                          <BusinessIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                                          <Typography variant="body2" color="text.secondary">
                                            {deal.hotel_name}
                                          </Typography>
                                        </Box>
                                      )}

                                      {deal.location && (
                                        <Box display="flex" alignItems="center">
                                          <LocationOnIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                                          <Typography variant="body2" color="text.secondary">
                                            {deal.location}
                                          </Typography>
                                        </Box>
                                      )}

                                      <Typography variant="body2" color="text.secondary">
                                        Added: {new Date(deal.created_at).toLocaleDateString('en-US', {
                                          year: 'numeric',
                                          month: 'short',
                                          day: 'numeric',
                                        })}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>

                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 2,
                                    bgcolor: alpha(theme.palette.background.paper, 0.4),
                                    borderLeft: { xs: 'none', md: `1px solid ${alpha(theme.palette.divider, 0.1)}` },
                                    borderTop: { xs: `1px solid ${alpha(theme.palette.divider, 0.1)}`, md: 'none' }
                                  }}
                                >
                                  <Chip
                                    label={formatCurrency(deal.asking_price)}
                                    color="primary"
                                    sx={{ fontWeight: 'bold', fontSize: '0.9rem', height: 32, mr: 2 }}
                                  />

                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => navigate(`/deals/${deal.id}`)}
                                    sx={{
                                      borderRadius: 2,
                                      fontWeight: 600,
                                      '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.9),
                                        color: 'primary.contrastText',
                                        borderColor: 'transparent'
                                      }
                                    }}
                                  >
                                    View
                                  </Button>
                                </Box>
                              </Box>
                            </Card>
                          ))}
                        </Box>
                      ) : (
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  <TableSortLabel
                                    active={sort === 'name'}
                                    direction={sort === 'name' ? 'asc' : 'desc'}
                                    onClick={() => setSort('name')}
                                  >
                                    Name
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                  <TableSortLabel
                                    active={sort === 'hotel_name'}
                                    direction={sort === 'hotel_name' ? 'asc' : 'desc'}
                                    onClick={() => setSort('hotel_name')}
                                  >
                                    Hotel Name
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                  <TableSortLabel
                                    active={sort === 'location'}
                                    direction={sort === 'location' ? 'asc' : 'desc'}
                                    onClick={() => setSort('location')}
                                  >
                                    Location
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                  <TableSortLabel
                                    active={sort === 'asking_price'}
                                    direction={sort === 'asking_price' ? 'asc' : 'desc'}
                                    onClick={() => setSort('asking_price')}
                                  >
                                    Asking Price
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                  <TableSortLabel
                                    active={sort === 'created_at'}
                                    direction={sort === 'created_at' ? 'asc' : 'desc'}
                                    onClick={() => setSort('created_at')}
                                  >
                                    Created At
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredDeals.map((deal) => (
                                <TableRow key={deal.id}>
                                  <TableCell>{deal.name}</TableCell>
                                  <TableCell>{deal.hotel_name}</TableCell>
                                  <TableCell>{deal.location}</TableCell>
                                  <TableCell>{formatCurrency(deal.asking_price)}</TableCell>
                                  <TableCell>{new Date(deal.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })}</TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => navigate(`/deals/${deal.id}`)}
                                      sx={{
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.primary.main, 0.9),
                                          color: 'primary.contrastText',
                                          borderColor: 'transparent'
                                        }
                                      }}
                                    >
                                      View
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  )}
                </Box>
              </Card>
            </Fade>
          )}
        </Grid>
      </Grid>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this deal?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="primary" autoFocus disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DealList;